const subscriptionsApi = require('../api/subscriptions');

const MIN_SUBSCRIPTION_PLANS_UPDATE = 1000 * 60 * 5; // 5 minutes
const MIN_SUBSCRIPTION_UPDATE = 1000 * 60 * 30; // 30 minutes
const FETCH_SUBSCRIPTION_TIMEOUT = 1000 * 30; // 30 seconds

function fetchSubscriptionPlansInProgress() {
  return {
    type: 'FETCH_SUBSCRIPTION_PLANS_IN_PROGRESS',
    timestamp: Date.now(),
  };
}

function fetchSubscriptionPlansSuccessful(plans) {
  return {
    type: 'FETCH_SUBSCRIPTION_PLANS_SUCCESSFUL',
    plans,
    timestamp: Date.now(),
  };
}

function fetchSubscriptionPlansFailed(reason) {
  return {
    type: 'FETCH_SUBSCRIPTION_PLANS_FAILED',
    reason,
    timestamp: Date.now(),
  };
}

function fetchSubscriptionInProgress() {
  return {
    type: 'FETCH_SUBSCRIPTION_IN_PROGRESS',
    timestamp: Date.now(),
  };
}

function fetchSubscriptionSuccessful(subscription) {
  return {
    type: 'FETCH_SUBSCRIPTION_SUCCESSFUL',
    subscription,
    timestamp: Date.now(),
  };
}

function fetchSubscriptionFailed(reason) {
  return {
    type: 'FETCH_SUBSCRIPTION_FAILED',
    reason,
    timestamp: Date.now(),
  };
}

function fetchSubscriptionPlans() {
  return (dispatch, getState) => {
    dispatch(fetchSubscriptionPlansInProgress());

    const state = getState();
    return subscriptionsApi.fetchSubscriptionPlans(state.auth.apiUrl).then(
      (plans) => {
        dispatch(fetchSubscriptionPlansSuccessful(plans));
        return plans;
      },
      (error) => {
        dispatch(fetchSubscriptionPlansFailed(error));
        throw error;
      },
    );
  };
}

function shouldFetchSubscriptionPlans(state, skipOutdatedCheck) {
  const { fetchingInProgressSince, lastSuccessfulFetch } =
    state.subscriptionPlans;

  if (!fetchingInProgressSince) {
    // Should update if data is too old
    if (
      !skipOutdatedCheck &&
      (!lastSuccessfulFetch ||
        Date.now() - lastSuccessfulFetch > MIN_SUBSCRIPTION_PLANS_UPDATE)
    ) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_SUBSCRIPTION_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchSubscriptionPlansIfNeeded() {
  return (dispatch, getState) => {
    if (shouldFetchSubscriptionPlans(getState())) {
      return dispatch(fetchSubscriptionPlans());
    }
    return Promise.resolve();
  };
}

function fetchActiveSubscription() {
  return (dispatch, getState) => {
    dispatch(fetchSubscriptionInProgress());

    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;

    if (!token) {
      dispatch(fetchSubscriptionFailed('Not logged in.'));
      return Promise.resolve();
    }

    return subscriptionsApi.fetchActiveSubscription(apiUrl, token).then(
      (subscription) => {
        dispatch(fetchSubscriptionSuccessful(subscription));
        return subscription;
      },
      (error) => {
        dispatch(fetchSubscriptionFailed(error));
        throw error;
      },
    );
  };
}

function shouldFetchActiveSubscription(state, skipOutdatedCheck) {
  const { fetchingInProgressSince, lastSuccessfulFetch } = state.subscription;

  if (!fetchingInProgressSince) {
    // Should update if data is too old
    if (
      !skipOutdatedCheck &&
      (!lastSuccessfulFetch ||
        Date.now() - lastSuccessfulFetch > MIN_SUBSCRIPTION_UPDATE)
    ) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_SUBSCRIPTION_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchActiveSubscriptionIfNeeded() {
  return (dispatch, getState) => {
    if (shouldFetchActiveSubscription(getState())) {
      return dispatch(fetchActiveSubscription());
    }
    return Promise.resolve();
  };
}

function createSubscription(data) {
  return (dispatch, getState) => {
    const state = getState();

    const { apiUrl, token } = state.auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return subscriptionsApi
      .createSubscription(apiUrl, token, data)
      .then((response) => {
        dispatch(fetchSubscriptionSuccessful(response));
        return response;
      });
  };
}

function cancelSubscription(id) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;

    return subscriptionsApi.cancelSubscription(apiUrl, token, id)
      .then(() => {
        dispatch(fetchActiveSubscription());
      }, (error) => {
        throw error;
      });
  };
}

module.exports = {
  fetchSubscriptionPlansIfNeeded,
  fetchSubscriptionPlans,
  fetchActiveSubscription,
  fetchActiveSubscriptionIfNeeded,
  createSubscription,
  cancelSubscription,
};
